import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../data";
import { useAction, useRueckfrage } from "../../components/ui";
import { date } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { useAgenturen } from "./hooks";

/**
 * Rabattcodes (0205, Inhaber 13.10.2026): Prozent, Laufzeit, Gültigkeit, Anzahl Einlösungen, optional an ein Konto gebunden.
 * Die Serverfunktion „konto-dienste“ legt Coupon und Promotion Code bei Stripe an; einlösbar im Bezahlvorgang
 * („Gutscheincode hinzufügen“) und im Kundenportal.
 */
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
type Code = { id: string; code: string; percent: number; months: number | null; valid_until: string | null; max_redemptions: number | null; org_id: string | null; org_name: string | null; note: string | null; active: boolean; created_at: string };

function Kopieren({ wert }: { wert: string }) {
  const [ok, setOk] = useState(false);
  return <button type="button" onClick={() => void navigator.clipboard.writeText(wert).then(() => { setOk(true); setTimeout(() => setOk(false), 1500); })}>{ok ? "Kopiert ✓" : "Kopieren"}</button>;
}

export function Rabattcodes() {
  const q = useQuery({ queryKey: ["platform_discount_codes"], queryFn: async () => parse<Code[]>(await api.read("platform_discount_codes", {})) });
  const konten = useAgenturen();
  const { busy, error, run } = useAction();
  const rueckfrage = useRueckfrage();
  const [code, setCode] = useState("");
  const [prozent, setProzent] = useState("20");
  const [monate, setMonate] = useState("6");
  const [bis, setBis] = useState("");
  const [max, setMax] = useState("1");
  const [org, setOrg] = useState("");
  const [notiz, setNotiz] = useState("");

  const anlegen = () => run("neu", async () => {
    if (!api.kontoDienste) throw new Error("Rabattcodes brauchen die Serverfunktion (nicht in der Demo).");
    await api.kontoDienste({ action: "rabatt_anlegen", code: code.trim() || null, prozent: Number(prozent), monate: monate.trim() ? Number(monate) : null,
      gueltig_bis: bis || null, max: max.trim() ? Number(max) : null, org: org || null, notiz: notiz.trim() || null });
    setCode(""); setNotiz("");
    await q.refetch();
  }, "Rabattcode angelegt und mit Stripe verbunden.");
  const aus = async (c: Code) => {
    if (!(await rueckfrage.ask({ title: "Code abschalten?", text: `„${c.code}“ lässt sich danach nicht mehr einlösen. Schon eingelöste Rabatte laufen weiter.`, confirmLabel: "Abschalten", danger: true }))) return;
    void run(c.id, async () => { await api.kontoDienste!({ action: "rabatt_aus", id: c.id }); await q.refetch(); }, "Abgeschaltet.");
  };

  return (
    <>
      <title>Rabattcodes · Plattform · CreatorOffice</title>
      {rueckfrage.element}
      <h1>Rabattcodes</h1>
      <p>Der Code wird sofort bei Stripe angelegt. Kunden lösen ihn im Bezahlvorgang unter „Gutscheincode hinzufügen“ ein oder im Kundenportal (Rechnungen und Zahlungsart verwalten).</p>

      <section className="panel stack" aria-labelledby="neu-h">
        <h2 id="neu-h">Neuer Code</h2>
        <label className="stack" style={{ gap: 4 }}>Code (leer = wird erzeugt)<input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={40} placeholder="z. B. SQUARE20" /></label>
        <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
          <label className="stack" style={{ gap: 4 }}>Rabatt in %<input type="number" min={1} max={100} value={prozent} onChange={(e) => setProzent(e.target.value)} inputMode="numeric" /></label>
          <label className="stack" style={{ gap: 4 }}>Laufzeit in Monaten (leer = einmalig)<input type="number" min={1} max={36} value={monate} onChange={(e) => setMonate(e.target.value)} inputMode="numeric" /></label>
          <label className="stack" style={{ gap: 4 }}>Einlösbar bis (leer = unbegrenzt)<input type="date" value={bis} onChange={(e) => setBis(e.target.value)} /></label>
          <label className="stack" style={{ gap: 4 }}>Anzahl Einlösungen (leer = beliebig)<input type="number" min={1} value={max} onChange={(e) => setMax(e.target.value)} inputMode="numeric" /></label>
        </div>
        <label className="stack" style={{ gap: 4 }}>Nur für dieses Konto (optional)
          <select value={org} onChange={(e) => setOrg(e.target.value)}>
            <option value="">alle Konten</option>
            {(konten.data?.rows ?? []).map((k) => <option key={k.org_id} value={k.org_id}>{k.name}</option>)}
          </select>
        </label>
        {org && <small>Die Bindung geht erst, wenn das Konto schon einmal den Bezahlvorgang geöffnet hat (Stripe-Kunde vorhanden). Sonst ohne Bindung anlegen und auf eine Einlösung begrenzen.</small>}
        <label className="stack" style={{ gap: 4 }}>Notiz (intern)<input value={notiz} onChange={(e) => setNotiz(e.target.value)} maxLength={300} placeholder="z. B. Rückmeldung nach Test, zugesagt per Mail" /></label>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div><button type="button" className="primary" disabled={!!busy || !prozent.trim()} aria-busy={busy === "neu"} onClick={() => void anlegen()}>{busy === "neu" ? "Wird angelegt …" : "Code anlegen"}</button></div>
      </section>

      <h2>Vorhandene Codes</h2>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : q.isPending ? <Laden /> : (q.data ?? []).length === 0 ? (
        <div className="panel tk-empty"><p>Noch keine Rabattcodes.</p></div>
      ) : (
        <div className="tablewrap">
          <table>
            <caption className="sr-only">Rabattcodes</caption>
            <thead><tr><th scope="col">Code</th><th scope="col">Rabatt</th><th scope="col">Einlösbar bis</th><th scope="col">Einlösungen</th><th scope="col">Konto</th><th scope="col">Status</th></tr></thead>
            <tbody>
              {q.data!.map((c) => (
                <tr key={c.id}>
                  <td><code>{c.code}</code> <Kopieren wert={c.code} />{c.note && <><br /><small>{c.note}</small></>}</td>
                  <td>{c.percent} % · {c.months ? `${c.months} Monate` : "einmalig"}</td>
                  <td>{c.valid_until ? date(c.valid_until) : "unbegrenzt"}</td>
                  <td>{c.max_redemptions ?? "beliebig"}</td>
                  <td>{c.org_name ?? "alle"}</td>
                  <td>{c.active ? <button type="button" disabled={!!busy} onClick={() => void aus(c)}>Abschalten</button> : <span className="pill">abgeschaltet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
